import { ApolloError, gql, useSubscription } from '@apollo/client';
import { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useDatadog } from '~/lib/hooks/useDatadog';
import {
  SafetyRiskAlert,
  SafetyRiskAlertStatus,
} from '~/lib/types/survey/SafetyRiskAlert';
import { subscribeAcknowledged } from '~/lib/util/safetyAlertBroadcast.util';
import { useSafetyAlertsDispatch } from '~/store/reducers/safetyAlerts';
import { useGetSafetyRiskAlertsQuery } from '~/store/services/assessments';

type OnSafetyRiskAlertData = {
  onSafetyRiskAlert: SafetyRiskAlert;
};

const ON_SAFETY_RISK_ALERT_SUBSCRIPTION = gql`
  subscription OnSafetyRiskAlert($providerId: String!) {
    onSafetyRiskAlert(providerId: $providerId) {
      safetyRiskAlertId
      surveyResponseExternalId
      patientId
      patientName
      completedByUserId
      providerId
      triggerReasonCode
    }
  }
`;

const SafetyRiskAlertSubscriptionManager = () => {
  const { currentUser } = useGlobalContext();
  const { logError } = useDatadog();
  const { phq9Enabled } = useFlags<LDFlags>();
  const { alertsHydrated, alertReceived, alertAcknowledged } =
    useSafetyAlertsDispatch();

  const skip = !phq9Enabled || !currentUser?.userId;

  const { data: hydratedAlerts } = useGetSafetyRiskAlertsQuery(
    {
      providerId: currentUser?.userId ?? '',
      statuses: [SafetyRiskAlertStatus.CREATED, SafetyRiskAlertStatus.VIEWED],
    },
    { skip },
  );

  const { data: subscriptionData } = useSubscription<OnSafetyRiskAlertData>(
    ON_SAFETY_RISK_ALERT_SUBSCRIPTION,
    {
      onError: (error: ApolloError) => {
        logError(error, { logFrom: 'Safety-risk-alert subscription' });
      },
      shouldResubscribe: true,
      skip,
      variables: { providerId: currentUser?.userId },
    },
  );

  useEffect(() => {
    if (hydratedAlerts) {
      alertsHydrated(hydratedAlerts);
    }
  }, [hydratedAlerts, alertsHydrated]);

  useEffect(() => {
    if (subscriptionData?.onSafetyRiskAlert) {
      alertReceived(subscriptionData.onSafetyRiskAlert);
    }
  }, [subscriptionData, alertReceived]);

  useEffect(() => {
    if (skip) {
      return undefined;
    }
    return subscribeAcknowledged((safetyRiskAlertId) => {
      alertAcknowledged(safetyRiskAlertId);
    });
  }, [skip, alertAcknowledged]);

  return null;
};

export default SafetyRiskAlertSubscriptionManager;
